import { DIALOGUE_MARK_ICONS, isCheckMark, type DialogueMark } from "odyssey-shared";
import { EN } from "odyssey-lang";
import { say } from "../../words.ts";
import type { DialogueMarkView } from "../../ui/kit/index.ts";
import type { GlyphName } from "../../ui/kit/Glyph.tsx";
import { iconArt } from "../icons/art.ts";
import type { IconsView } from "../icons/context.tsx";
import type { ChoiceMarks } from "./choice-marks.ts";

/**
 * What a mark's tooltip names beyond the mark: a check's chance as a whole percent, the background a background
 * choice is open to, and what a locked choice needs. Each is absent until the dialogue frame carries it (ADR-136
 * amendment of 2026-10-28, rows P2.22-T12 and T13); the line it fills is then left out.
 */
export interface MarkFacts {
  readonly percent?: number;
  readonly background?: string;
  readonly need?: string;
}

/**
 * The glyph a mark draws where the page has no art for it.
 */
const FALLBACK: Readonly<Record<DialogueMark, GlyphName>> = {
  might: "info-box",
  agility: "info-box",
  vitality: "info-box",
  intellect: "info-box",
  willpower: "info-box",
  perception: "eye-small",
  presence: "info-box",
  background: "script-text",
  locked: "warning-box",
  significant: "trending-up",
  defining: "trending-up",
  leave: "close-small",
};

/**
 * A mark's tooltip lines: for a check its name, the attribute it reads and its chance; for the rest its name and,
 * where known, what it is open to or needs.
 *
 * @param mark The mark.
 * @param facts What the frame says about it.
 * @returns The lines, the first the mark's name.
 */
export function markTip(mark: DialogueMark, facts: MarkFacts = {}): readonly string[] {
  if (isCheckMark(mark)) {
    const attribute = say(`talk.attributes.${mark}`);
    const lines = [say("talk.marks.check", { attribute }), say("talk.marks.stat", { attribute })];
    return facts.percent === undefined ? lines : [...lines, say("talk.marks.chance", { percent: String(facts.percent) })];
  }
  switch (mark) {
    case "background":
      return facts.background === undefined ? [say("talk.marks.background")] : [say("talk.marks.background"), say("talk.marks.backgroundNeeds", { background: facts.background })];
    case "locked":
      return facts.need === undefined ? [say("talk.marks.locked")] : [say("talk.marks.locked"), say("talk.marks.lockedNeeds", { need: facts.need })];
    case "significant":
      return [say("talk.marks.significant")];
    case "defining":
      return [say("talk.marks.defining")];
    case "leave":
      return [say("talk.leave")];
  }
}

/**
 * A mark as the window draws it: its art in the 16 box when the page's atlas has it, its tooltip lines.
 *
 * @param icons The page's icons.
 * @param mark The mark.
 * @param facts What the frame says about it.
 * @returns The mark's view.
 */
export function markView(icons: IconsView, mark: DialogueMark, facts: MarkFacts = {}): DialogueMarkView {
  return { art: iconArt(icons.snapshot, DIALOGUE_MARK_ICONS[mark], `dialogue mark ${mark}`, 16, icons.log), glyph: FALLBACK[mark], tip: markTip(mark, facts) };
}

/**
 * A background's name from its rules id (`fallen_noble` is "Fallen Noble"), or a fixed line for one the language
 * files do not name, so a player never sees a rules id.
 *
 * @param id The background's id in the rules table `backgrounds`.
 * @returns The name.
 */
export function backgroundName(id: string): string {
  const key = id.replace(/_([a-z0-9])/g, (_match, letter: string) => letter.toUpperCase());
  return Object.hasOwn(EN.words.talk.backgrounds, key) ? say(`talk.backgrounds.${key as keyof typeof EN.words.talk.backgrounds}`) : say("talk.backgrounds.unknown");
}

/**
 * A choice's marks as the window draws them, each told only its own fact: a check its chance, a background mark the
 * background, a locked mark the need.
 *
 * @param icons The page's icons.
 * @param choice The choice's marks from the frame.
 * @returns One view per mark, in the frame's order.
 */
export function choiceMarkViews(icons: IconsView, choice: ChoiceMarks): readonly DialogueMarkView[] {
  return choice.marks.map((mark) => {
    if (isCheckMark(mark)) {
      return markView(icons, mark, choice.percent === undefined ? {} : { percent: choice.percent });
    }
    if (mark === "background") {
      return markView(icons, mark, choice.background === undefined ? {} : { background: backgroundName(choice.background) });
    }
    if (mark === "locked") {
      return markView(icons, mark, choice.need === undefined ? {} : { need: choice.need });
    }
    return markView(icons, mark);
  });
}
